import { useSession } from "@/services/auth";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

// Tracking response types (matching admin-web)
export interface TrackingEvent {
  timestamp: string;
  status: string;
  description: string;
  location?: string;
  city?: string;
  province?: string;
  country?: string;
  source?: string;
  metadata?: Record<string, unknown>;
}

export interface CarrierTracking {
  trackingNumber: string;
  carrier: string;
  carrierName?: string;
  carrierTrackingUrl?: string;
  serviceCode?: string;
}

export interface PackageInfo {
  number: number;
  trackingNumber: string;
  carrierTracking?: string;
  status: string;
  weight?: string;
  dimensions?: string;
  description?: string;
  isFragile?: boolean;
  requiresCooling?: boolean;
  declaredValue?: string;
}

export interface AnticipatedEvent {
  status: string;
  description: string;
  isAnticipated: true;
}

export interface EstimatedMilestone {
  type: "CARRIER_DROPOFF" | "CUSTOMER_DELIVERY";
  label: string;
  estimate: string | null;
  /** Waypoint ETA instant; optional while the server update rolls out. */
  estimatedAt?: string | null;
  /** Calendar-date promise (YYYY-MM-DD), never an instant. */
  estimatedDate?: string | null;
  /** Display timezone of the server's preformatted estimate; optional during rollout. */
  timeZone?: string;
  timeWindow: {
    start: string;
    end: string;
  } | null;
  placeholder: string;
  isComplete: boolean;
  completedAt: string | null;
}

export interface TrackingResponse {
  // Primary tracking
  trackingNumber: string;
  shipmentNumber?: number;

  // Carrier tracking info
  carrierTracking?: CarrierTracking | null;

  // Current status
  currentStatus: string;
  status: string; // Legacy field
  lastUpdated: string;

  // Shipment details
  shipmentDetails: {
    shipmentId?: string;
    orderId?: string;
    orderNumber?: number;
    estimatedDelivery?: string;
    actualDelivery?: string;
    recipientName?: string;
    recipientAddress: string;
    senderName?: string;
    senderAddress?: string;
    packageCount: number;
    totalWeight?: number;
  };

  // Tracking timeline
  trackingEvents: TrackingEvent[];

  // Anticipated events (future steps)
  anticipatedEvents?: AnticipatedEvent[];

  // Estimated milestone (ETA display)
  estimatedMilestone?: EstimatedMilestone;

  // Package details
  packages: PackageInfo[];

  // Metadata
  metadata?: {
    correlationId: string;
    carrierId: string;
    isLocalDelivery: boolean;
    hasMultiplePieces: boolean;
    carrierHandoffs: number;
  };
}

/**
 * Get tracking information by tracking number (public endpoint).
 * Accepts both EZX and carrier tracking numbers.
 */
export function useTracking(trackingNumber: string | undefined) {
  const { token, customer, isLoading } = useSession();
  // The opaque cache scope changes with credentials; never put a bearer in a query key.
  const session = useMemo(() => ({ token, key: crypto.randomUUID() }), [token]);
  const privateView = !!session.token && !!customer?.id && !isLoading;
  return useQuery({
    queryKey: [
      "tracking",
      trackingNumber,
      privateView ? "customer" : "public",
      privateView ? customer.id : null,
      session.key,
    ],
    queryFn: async ({ signal }) => {
      if (!trackingNumber) throw new Error("Tracking number is required");
      const number = encodeURIComponent(trackingNumber);
      if (privateView) {
        try {
          const { data } = await axios.get<TrackingResponse>(
            `${API_URL}/api/v2/customer/tracking/${number}`,
            { headers: { Authorization: `Bearer ${session.token}` }, signal }
          );
          return data;
        } catch (error) {
          // Another customer's number is still publicly trackable, never privately readable.
          if (!axios.isAxiosError(error) || error.response?.status !== 404)
            throw error;
        }
      }
      const { data } = await axios.get<TrackingResponse>(
        `${API_URL}/api/v2/shipping/tracking/${number}`,
        { signal }
      );
      return data;
    },
    enabled: !!trackingNumber && !isLoading,
    gcTime: 0,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
    staleTime: 30_000,
    retry: false,
  });
}
